import React, { useState, useMemo } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FiMonitor, FiShoppingCart, FiSmartphone, FiSettings, FiArrowRight, FiX, FiCheck } from 'react-icons/fi';
import QuoteEstimator from '../QuoteEstimator';


// Animations
const pulse = keyframes`
  0%, 100% { opacity: 0.4; transform: scale(1); }
  50% { opacity: 0.8; transform: scale(1.05); }
`;

const gradientMove = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const ServicesContainer = styled.section`
  padding: 8rem 0;
  background: linear-gradient(160deg, #1a3a5c 0%, #1e4976 50%, #2471a3 100%);
  position: relative;
  overflow: hidden;
  width: 100%;
  scroll-margin-top: 70px;
  
  body.dark-mode & {
    background: #0e1420;
  }
  
  @media (max-width: 768px) {
    padding: 4rem 0;
    scroll-margin-top: 60px;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 140px;
    pointer-events: none;
    z-index: 1;

    body.dark-mode & {
      background: linear-gradient(180deg, rgba(14, 20, 32, 0.95) 0%, rgba(14, 20, 32, 0) 100%);
    }
  }
`;

// Éléments décoratifs flottants
const FloatingOrb = styled.div`
  position: absolute;
  border-radius: 50%;
  filter: blur(60px);
  animation: ${pulse} 8s ease-in-out infinite;
  pointer-events: none;
  
  &.orb1 {
    width: 400px;
    height: 400px;
    background: linear-gradient(135deg, rgba(254, 209, 54, 0.15), rgba(200, 16, 46, 0.1));
    top: -100px;
    right: -100px;
    animation-delay: 0s;
  }
  
  &.orb2 {
    width: 300px;
    height: 300px;
    background: linear-gradient(135deg, rgba(66, 133, 244, 0.1), rgba(52, 168, 83, 0.1));
    bottom: 20%;
    left: -100px;
    animation-delay: 2s;

    body.dark-mode & {
      background: linear-gradient(135deg, rgba(59, 130, 246, 0.08), rgba(37, 99, 235, 0.04));
    }
  }
  
  &.orb3 {
    width: 200px;
    height: 200px;
    background: linear-gradient(135deg, rgba(200, 16, 46, 0.1), rgba(254, 209, 54, 0.15));
    top: 50%;
    right: 10%;
    animation-delay: 4s;

    body.dark-mode & {
      background: linear-gradient(135deg, rgba(200, 16, 46, 0.06), rgba(254, 209, 54, 0.1));
    }
  }
`;

const SectionStarsWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 0;
  overflow: hidden;
  display: block;
`;

const StarDot = styled.div`
  position: absolute;
  border-radius: 50%;
  background: white;
`;

function SectionStars({ count = 150 }) {
  const stars = useMemo(() => {
    const arr = [];
    for (let i = 0; i < count; i++) {
      const size = Math.random() * 2 + 0.5;
      arr.push({
        id: i,
        left: `${Math.random() * 100}%`,
        top: `${Math.random() * 100}%`,
        width: size,
        height: size,
        opacity: Math.random() * 0.45 + 0.08,
        animDelay: Math.random() * 6,
        animDuration: Math.random() * 4 + 3,
      });
    }
    return arr;
  }, [count]);

  return (
    <SectionStarsWrapper>
      {stars.map(s => (
        <StarDot
          key={s.id}
          style={{
            left: s.left,
            top: s.top,
            width: s.width,
            height: s.height,
            opacity: s.opacity,
            animation: `twinkle ${s.animDuration}s ease-in-out ${s.animDelay}s infinite alternate`,
          }}
        />
      ))}
    </SectionStarsWrapper>
  );
}

const SectionHeader = styled.div`
  text-align: center;
  max-width: 900px;
  margin: 0 auto 5rem;
  padding: 0 2rem;
  position: relative;
  z-index: 2;
  
  @media (max-width: 768px) {
    margin-bottom: 3rem;
    padding: 0 1.5rem;
  }
  
  @media (max-width: 480px) {
    padding: 0 1rem;
  }
`;

const SectionLogo = styled(motion.img)`
  width: 100px;
  height: 100px;
  margin: 0 auto 2rem;
  display: block;
  filter: grayscale(100%) brightness(1.5);
  opacity: 0.7;
  
  @media (max-width: 768px) {
    width: 80px;
    height: 80px;
  }
`;

const SectionTitle = styled(motion.h2)`
  font-size: 3.5rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  color: var(--white);
  line-height: 1.2;
  
  span {
    background: linear-gradient(135deg, var(--secondary) 0%, var(--accent) 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
  
  @media (max-width: 480px) {
    font-size: 2rem;
  }
`;

const SectionDescription = styled(motion.p)`
  color: rgba(255, 255, 255, 0.75);
  font-size: 1.25rem;
  line-height: 1.8;
  max-width: 700px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const ServicesGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 2;
  
  @media (max-width: 768px) {
    padding: 0 1.5rem;
    gap: 1rem;
  }
  
  @media (max-width: 480px) {
    padding: 0 1rem;
  }
`;

const ServiceCard = styled(motion.div)`
  background: var(--white);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  padding: 1.5rem 2rem;
  display: flex;
  align-items: center;
  gap: 2rem;
  cursor: pointer;
  border: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  body.dark-mode & {
    background: #0e1420;
    border: 1px solid rgba(255, 255, 255, 0.08);
    box-shadow: none;
  }
  
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background: ${props => props.color};
    transform: scaleY(0);
    transition: transform 0.3s ease;
  }
  
  &:hover {
    transform: translateX(8px);
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
    
    body.dark-mode & {
      box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
    }
    
    &::before {
      transform: scaleY(1);
    }
  }
  
  @media (max-width: 768px) {
    padding: 1rem 1.2rem;
    gap: 1rem;
    border-radius: 12px;
    
    &:hover {
      transform: none;
    }
    
    &::before {
      transform: scaleY(1);
    }
  }
  
  @media (max-width: 576px) {
    padding: 0.8rem 1rem;
    gap: 0.8rem;
  }
`;

const ServiceIcon = styled.div`
  width: 56px;
  height: 56px;
  min-width: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, ${props => props.color}, ${props => props.colorEnd || props.color});
  border-radius: 14px;
  
  svg {
    font-size: 1.5rem;
    color: white;
  }
  
  @media (max-width: 768px) {
    width: 42px;
    height: 42px;
    min-width: 42px;
    border-radius: 10px;
    
    svg {
      font-size: 1.1rem;
    }
  }
  
  @media (max-width: 576px) {
    width: 38px;
    height: 38px;
    min-width: 38px;
    border-radius: 8px;
    
    svg {
      font-size: 1rem;
    }
  }
`;

const ServiceContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const ServiceTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--primary);
  margin-bottom: 0.4rem;
  
  body.dark-mode & {
    color: var(--white);
  }
`;

const ServiceDescription = styled.p`
  color: #555;
  font-size: 0.95rem;
  line-height: 1.5;
  margin: 0;
  
  body.dark-mode & {
    color: rgba(255, 255, 255, 0.65);
  }
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const ServiceMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    gap: 1rem;
  }
  
  @media (max-width: 576px) {
    gap: 0.5rem;
  }
`;

const ServicePrice = styled.div`
  text-align: right;
  
  @media (max-width: 576px) {
    text-align: left;
  }
`;

const PriceLabel = styled.span`
  display: block;
  font-size: 0.75rem;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.2rem;
  
  body.dark-mode & {
    color: rgba(255, 255, 255, 0.5);
  }
`;

const PriceValue = styled.span`
  font-size: 1.1rem;
  font-weight: 700;
  color: ${props => props.color};
`;

const ServiceArrow = styled.div`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => `rgba(${props.bgColor}, 0.1)`};
  border-radius: 10px;
  color: ${props => props.color};
  transition: all 0.3s ease;
  
  svg {
    font-size: 1.1rem;
    transition: transform 0.3s ease;
  }
  
  @media (max-width: 768px) {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    
    svg {
      font-size: 0.9rem;
    }
  }
  
  ${ServiceCard}:hover & {
    background: ${props => props.color};
    color: white;
    
    svg {
      transform: translateX(3px);
    }
  }
`;

// Section Devis
const DevisSection = styled.div`
  max-width: 1200px;
  margin: 6rem auto 0;
  padding: 0 2rem;
  position: relative;
  z-index: 2;
  scroll-margin-top: 90px;
  
  @media (max-width: 768px) {
    margin-top: 4rem;
    padding: 0 1.5rem;
    scroll-margin-top: 80px;
  }
  
  @media (max-width: 480px) {
    margin-top: 3rem;
    padding: 0 1rem;
  }
`;

const DevisCard = styled(motion.div)`
  border-radius: 32px;
  padding: 4rem;
  position: relative;
  overflow: hidden;
  
  @media (max-width: 768px) {
    padding: 2.5rem 1.5rem;
    border-radius: 24px;
  }
`;

const DevisHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
  position: relative;
  z-index: 1;
`;

const DevisTitle = styled.h3`
  font-size: 2.5rem;
  font-weight: 800;
  color: white;
  margin-bottom: 1rem;
  
  span {
    color: var(--secondary);
  }
  
  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
`;

const DevisSubtitle = styled.p`
  color: rgba(255, 255, 255, 0.7);
  font-size: 1.15rem;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
`;

const QuoteWrapper = styled.div`
  position: relative;
  z-index: 1;
`;

// Modal
const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(10px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 2rem;
  
  @media (max-width: 768px) {
    padding: 1rem;
    align-items: flex-end;
  }
`;

const ModalContent = styled(motion.div)`
  background: white;
  border-radius: 24px;
  width: 100%;
  max-width: 700px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  
  body.dark-mode & {
    background: #0e1420;
  }
  
  @media (max-width: 768px) {
    max-height: 80vh;
    border-radius: 20px 20px 0 0;
  }
`;

const ModalHeader = styled.div`
  padding: 1.5rem 2rem 1.2rem;
  background: linear-gradient(135deg, ${props => props.color} 0%, ${props => props.colorEnd || props.color} 100%);
  position: relative;
  display: flex;
  align-items: center;
  gap: 1rem;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 20px;
    background: linear-gradient(to bottom, transparent, ${props => props.isDark ? '#0e1420' : 'white'});
  }
  
  @media (max-width: 768px) {
    padding: 1rem 1.2rem 1rem;
  }
`;

const ModalIconLarge = styled.div`
  width: 48px;
  height: 48px;
  min-width: 48px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  
  svg {
    font-size: 1.4rem;
    color: white;
  }
  
  @media (max-width: 768px) {
    width: 38px;
    height: 38px;
    min-width: 38px;
    border-radius: 10px;
    
    svg {
      font-size: 1.1rem;
    }
  }
`;

const ModalTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: white;
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: rotate(90deg);
  }
  
  svg {
    font-size: 1.2rem;
  }
`;

const ModalBody = styled.div`
  padding: 2rem 2.5rem 2.5rem;
  
  @media (max-width: 768px) {
    padding: 1.2rem 1.2rem 1.5rem;
  }
`;

const ModalDescription = styled.p`
  color: var(--gray-600);
  font-size: 1.1rem;
  line-height: 1.8;
  margin-bottom: 2rem;
  white-space: pre-line;
  
  body.dark-mode & {
    color: var(--gray-300);
  }
  
  @media (max-width: 768px) {
    font-size: 0.95rem;
    line-height: 1.6;
    margin-bottom: 1.2rem;
  }
`;

const ModalFeaturesTitle = styled.h4`
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--dark);
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  body.dark-mode & {
    color: var(--white);
  }
  
  &::before {
    content: '';
    width: 4px;
    height: 20px;
    background: ${props => props.color};
    border-radius: 2px;
  }
  
  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 0.8rem;
  }
`;

const ModalFeaturesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 1.5rem 0;
  display: grid;
  gap: 0.6rem;
  
  @media (max-width: 768px) {
    margin: 0 0 1rem 0;
    gap: 0.5rem;
  }
`;

const ModalFeatureItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.8rem;
  background: ${props => `rgba(${props.bgColor}, 0.05)`};
  border-radius: 10px;
  color: var(--dark);
  font-size: 0.95rem;
  
  @media (max-width: 768px) {
    padding: 0.6rem 0.8rem;
    gap: 0.6rem;
    font-size: 0.88rem;
    border-radius: 8px;
  }
  
  body.dark-mode & {
    color: var(--gray-200);
    background: ${props => `rgba(${props.bgColor}, 0.1)`};
  }
  
  svg {
    flex-shrink: 0;
    width: 24px;
    height: 24px;
    padding: 4px;
    background: ${props => props.color};
    color: white;
    border-radius: 50%;
    margin-top: 2px;
  }
`;

const ModalCTA = styled.button`
  width: 100%;
  padding: 1.25rem 2rem;
  background: linear-gradient(135deg, ${props => props.color} 0%, ${props => props.colorEnd || props.color} 100%);
  color: white;
  font-size: 1.1rem;
  font-weight: 600;
  
  @media (max-width: 768px) {
    padding: 1rem 1.5rem;
    font-size: 0.95rem;
  }
  border: none;
  border-radius: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  transition: all 0.3s ease;
  box-shadow: 0 10px 30px ${props => `rgba(${props.bgColor}, 0.3)`};
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 15px 40px ${props => `rgba(${props.bgColor}, 0.4)`};
  }
  
  svg {
    transition: transform 0.3s ease;
  }
  
  &:hover svg {
    transform: translateX(4px);
  }
`;

const ServicesSection = () => {
  const [selectedService, setSelectedService] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const openModal = (service) => {
    setSelectedService(service);
    setModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setModalOpen(false);
    document.body.style.overflow = 'auto';
  };

  const services = [
    {
      id: 1,
      icon: <FiMonitor />,
      title: 'Site Vitrine',
      description: 'Présentez votre activité avec un site professionnel qui inspire confiance.',
      startingPrice: 'Dès 990€',
      content: `Un site vitrine efficace qui convertit vos visiteurs en clients. Design moderne, chargement rapide et parfaitement adapté aux mobiles.

Idéal pour les indépendants, PME et professions libérales qui veulent une présence en ligne professionnelle.`,
      allFeatures: [
        'Design sur mesure adapté à votre image',
        'Optimisé pour Google (SEO)',
        'Formulaire de contact intégré',
        'Compatible mobiles et tablettes',
        'Formation à la gestion du contenu'
      ],
      color: '#E67E22',
      colorEnd: '#D35400',
      bgColor: '230, 126, 34'
    },
    {
      id: 2,
      icon: <FiShoppingCart />,
      title: 'E-commerce',
      description: 'Vendez en ligne 24h/24 avec une boutique sécurisée et facile à gérer.',
      startingPrice: 'Dès 2.490€',
      content: `Une boutique en ligne complète pour développer vos ventes. Gestion simple des produits, paiements sécurisés et suivi des commandes.

Solution clé en main pour lancer ou développer votre activité de vente en ligne.`,
      allFeatures: [
        'Gestion facile de vos produits',
        'Paiements Bancontact, Visa, PayPal...',
        'Calcul automatique des frais de port',
        'Suivi des commandes et stocks',
        'Statistiques de vente'
      ],
      color: '#C8102E',
      colorEnd: '#a00d25',
      bgColor: '200, 16, 46'
    },
    {
      id: 3,
      icon: <FiSmartphone />,
      title: 'Application Web',
      description: 'Automatisez vos processus avec un outil sur mesure adapté à votre métier.',
      startingPrice: 'Sur devis',
      content: `Des applications web personnalisées pour digitaliser votre activité. Gestion clients, réservations, facturation... tout ce dont vous avez besoin.

Solutions évolutives qui grandissent avec votre entreprise.`,
      allFeatures: [
        'Analyse de vos besoins',
        'Développement sur mesure',
        'Interface intuitive',
        'Formation complète',
        'Maintenance et évolutions'
      ],
      color: '#4285F4',
      colorEnd: '#2b6ed9',
      bgColor: '66, 133, 244'
    },
    {
      id: 4,
      icon: <FiSettings />,
      title: 'Maintenance & SEO',
      description: 'Gardez votre site sécurisé, à jour et visible sur Google.',
      startingPrice: 'Dès 79€/mois',
      content: `Un site web nécessite un entretien régulier pour rester performant et sécurisé. Je m'occupe de tout pour que vous puissiez vous concentrer sur votre activité.

Mises à jour, sauvegardes, sécurité et amélioration continue de votre référencement.`,
      allFeatures: [
        'Mises à jour et sauvegardes',
        'Surveillance de la sécurité',
        'Optimisation des performances',
        'Amélioration du référencement',
        'Rapport mensuel'
      ],
      color: '#34A853',
      colorEnd: '#2d9148',
      bgColor: '52, 168, 83'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 1 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 1, y: 0 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0,
        ease: [0.25, 0.1, 0.25, 1]
      }
    }
  };

  return (
    <ServicesContainer id="services">
      <FloatingOrb className="orb1" />
      <FloatingOrb className="orb2" />
      <FloatingOrb className="orb3" />
      <SectionStars count={150} />
      
      <SectionHeader>
        <SectionTitle
          initial={{ opacity: 1, y: 0 }}
          animate={{ opacity: 1, y: 0 }}
        >
          Créons ensemble votre <span>succès digital</span>
        </SectionTitle>
        
      </SectionHeader>

      <motion.div
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
      >
        <ServicesGrid>
          {services.map((service) => (
            <ServiceCard
              key={service.id}
              variants={itemVariants}
              color={service.color}
              colorEnd={service.colorEnd}
              bgColor={service.bgColor}
              onClick={() => openModal(service)}
            >
              <ServiceIcon color={service.color} colorEnd={service.colorEnd}>
                {service.icon}
              </ServiceIcon>
              
              <ServiceContent>
                <ServiceTitle>{service.title}</ServiceTitle>
                <ServiceDescription>{service.description}</ServiceDescription>
              </ServiceContent>
              
              <ServiceMeta>
                <ServicePrice>
                  <PriceLabel>À partir de</PriceLabel>
                  <PriceValue color={service.color}>{service.startingPrice}</PriceValue>
                </ServicePrice>
                
                <ServiceArrow color={service.color} bgColor={service.bgColor}>
                  <FiArrowRight />
                </ServiceArrow>
              </ServiceMeta>
            </ServiceCard>
          ))}
        </ServicesGrid>
      </motion.div>

      {/* Section Devis */}
      <DevisSection id="devis">
        <DevisCard
          initial={{ opacity: 1, y: 0 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <DevisHeader>
            <DevisTitle>
              Estimez votre <span>projet</span> gratuitement
            </DevisTitle>
          </DevisHeader>
          
          <QuoteWrapper>
            <QuoteEstimator scrollToContact={() => {
              const contactSection = document.getElementById('contact');
              if (contactSection) {
                contactSection.scrollIntoView({ behavior: 'smooth' });
              }
            }} />
          </QuoteWrapper>
        </DevisCard>
      </DevisSection>

      <AnimatePresence>
        {modalOpen && selectedService && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeModal}
          >
            <ModalContent
              initial={{ opacity: 0, scale: 0.9, y: 50 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 50 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
            >
              <ModalHeader 
                color={selectedService.color} 
                colorEnd={selectedService.colorEnd}
                isDark={document.body.classList.contains('dark-mode')}
              >
                <CloseButton onClick={closeModal}>
                  <FiX />
                </CloseButton>
                <ModalIconLarge>
                  {selectedService.icon}
                </ModalIconLarge>
                <ModalTitle>{selectedService.title}</ModalTitle>
              </ModalHeader>
              
              <ModalBody>
                <ModalDescription>{selectedService.content}</ModalDescription>
                
                <ModalFeaturesTitle color={selectedService.color}>
                  Ce qui est inclus
                </ModalFeaturesTitle>
                
                <ModalFeaturesList>
                  {selectedService.allFeatures.map((feature, i) => (
                    <ModalFeatureItem 
                      key={i} 
                      color={selectedService.color}
                      bgColor={selectedService.bgColor}
                    >
                      <FiCheck />
                      {feature}
                    </ModalFeatureItem>
                  ))}
                </ModalFeaturesList>
                
                <ModalCTA 
                  color={selectedService.color}
                  colorEnd={selectedService.colorEnd}
                  bgColor={selectedService.bgColor}
                  onClick={() => {
                    closeModal();
                    const contactSection = document.getElementById('contact');
                    if (contactSection) {
                      contactSection.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                >
                  Discuter de mon projet <FiArrowRight />
                </ModalCTA>
              </ModalBody>
            </ModalContent>
          </ModalOverlay>
        )}
      </AnimatePresence>
    </ServicesContainer>
  );
};

export default ServicesSection;
